import { DEFAULT_CURRENCY, INIT_DATA_STORAGE_KEY } from '@/app/constants'
import { UserData } from '@/app/types'
import { calculateDisplayedMaxPrice } from '@/app/utils'
import { useLocalStorage } from '@uidotdev/usehooks'
import { Currencies } from 'currencies-map'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import ReactSlider from 'react-slider'
import PriceSliderThumb from './common/PriceSliderThumb'

interface PriceSliderProps {
	onValuesChange: (values: [number, number]) => void
	initialValues: [number, number]
	valuesRange: { min: number; max: number }
}

const PriceSlider: FC<PriceSliderProps> = ({
	onValuesChange,
	initialValues,
	valuesRange,
}) => {
	const [prevValues, setPrevValues] = useState<[number, number]>(initialValues)
	const [values, setValues] = useState<[number, number]>(initialValues)
	const query = useSearchParams()
	const [initData] = useLocalStorage<UserData>(INIT_DATA_STORAGE_KEY)
	const userCurrency = initData?.selectedCurrency
	const t = useTranslations('Keywords')
	const { min: minPrice, max: maxPrice } = valuesRange

	const currency =
		query.get('currency') || userCurrency?.abbreviation || DEFAULT_CURRENCY

	const currencySymbol = useMemo(
		() => Currencies.symbols.get(currency.toUpperCase()),
		[currency]
	)

	const displayedMaxPrice = useMemo(
		() => calculateDisplayedMaxPrice(minPrice, Math.max(maxPrice, values[1])),
		[minPrice, maxPrice, values]
	)

	if (initialValues && (initialValues[0] !== prevValues[0] || initialValues[1] !== prevValues[1])) {
		setPrevValues(initialValues)
		if (initialValues[0] >= initialValues[1]) {
			setValues([initialValues[0], initialValues[0] + 100])
		}
		setValues(initialValues)
	}

	const handleSliderChange = useCallback((newValues: [number, number]) => {
		setValues(newValues)
	}, [])

	const handleSliderAfterChange = useCallback(
		(newValues: [number, number]) => {
			setValues(newValues)
			onValuesChange([
				newValues[0],
				newValues[1] >= displayedMaxPrice ? maxPrice : newValues[1],
			])
		},
		[displayedMaxPrice, maxPrice, onValuesChange]
	)

	const minValue = `${currencySymbol}${
		values[0] <= minPrice ? `${values[0]}` : values[0]
	}`
	const maxValue = `${currencySymbol}${
		values[1] >= displayedMaxPrice ? `${displayedMaxPrice}+` : values[1]
	}`
	return (
		<div className="w-full">
			<div className="flex justify-between gap-2">
				<div className="flex w-auto flex-col items-start py-1">
					<span className="text-xs text-text-700 text-left w-full font-normal leading-[120%]">
						{t('min')}
					</span>
					<input
						type="text"
						readOnly
						value={minValue}
						className="text-base text-text-900 font-medium w-full outline-none leading-[120%] "
					/>
				</div>
				<div className="flex w-auto flex-col items-start py-1">
					<span className="text-xs text-text-700 text-right w-full font-normal leading-[120%]">
						{t('max')}
					</span>
					<input
						type="text"
						readOnly
						value={maxValue}
						className="text-base text-text-900 font-medium w-full outline-none text-right leading-[120%]"
					/>
				</div>
			</div>
			<ReactSlider
				className="horizontal-slider"
				trackClassName="example-track"
				min={minPrice}
				max={displayedMaxPrice}
				value={[values[0], Math.min(values[1], displayedMaxPrice)]}
				onAfterChange={handleSliderAfterChange}
				onChange={handleSliderChange}
				ariaLabel={['Minimum price', 'Maximum price']}
				ariaValuetext={(state) => `${state.valueNow} ${currency}`}
				step={1}
				renderThumb={(props, state) => {
					const { key, ...restProps } = props
					const filterId = `filter0_dd_${state.index}`
					return (
						<div
							key={key}
							{...restProps}
							className="price-slider__thumb-container"
							style={{ ...restProps.style, zIndex: 20 }}
						>
							<PriceSliderThumb filterId={filterId} />
						</div>
					)
				}}
				pearling
				minDistance={10}
			/>
		</div>
	)
}

export default PriceSlider
